import { AppBar, Typography, Box, Toolbar, Grid, Button } from "@mui/material";
import {
  useLocation,
  useMatch,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import Dropdown from "../../../components/dropdown";
import Search from "../../../components/search";
import CustomBreadcrumbs from "../../breadcrumbs";
import { useAppContext } from "../../../context/appContext";
import { useStyles } from "./styles";
import constants from "../../../constants/constants.json";
import React, { useEffect, useState } from "react";
import CustomTabs from "components/supplyAndDemand/Tabs/customTabs";
import { supplyAndDemandGetProductById } from "pages/supplyDemand/supplyDemand.service";
import { getToken } from "utils/loadAuth";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useTheme } from "@emotion/react";

const Header = ({
  isNavbarOpen,
  handleSearchQueryChange,
  searchQuery,
  children,
}) => {
  const { sortOption, setSortOption, handleFilterChange } = useAppContext();
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const theme = useTheme();
  const classes = useStyles({ isNavbarOpen, theme });
  const sortBy = [
    { Id: 1, Name: constants.sortByFavorites },
    { Id: 2, Name: constants.sortAsc },
    { Id: 3, Name: constants.sortDesc },
  ];
  const [product, setProduct] = useState();
  const [searchParams] = useSearchParams();

  const match = useMatch("/overview/entry/:pid/:fid?/:tid?/*");
  let { fid, pid, tid } = match?.params || 2;

  if (searchParams.get("id")) {
    tid = searchParams.get("id").split("/")?.pop();
  }

  const location = useLocation();
  const navigate = useNavigate();

  const isHomePage =
    location.pathname
      .split("/")
      .filter((x) => x)
      .map((x) => "/" + x).length === 0;

  const getProductById = React.useCallback(async () => {
    try {
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (token) {
        const response = await supplyAndDemandGetProductById({
          productId: pid,
          token,
        });
        const data = response.data;
        if (data?.items?.length) {
          setProduct(data.items[0]);
        } else {
          navigate("/notfound");
        }
      }
    } catch (error) {
      if (error.status === 403) {
        navigate("/forbidden");
      } else if (error.status === 401) {
        navigate("/unauthorized");
      }
      console.log("Error: ", error.message);
    }
  }, [pid, setProduct, getAccessTokenSilently, loginWithRedirect, navigate]);

  useEffect(() => {
    pid && getProductById();
  }, [getProductById, pid]);

  useEffect(() => {
    const validFunctionIds = [1, 2, 5];

    if (fid && !validFunctionIds.includes(Number(fid))) navigate("/notfound");
  }, [fid, navigate]);

  const isDrilldown = location.pathname.includes("overview");
  return (
    <AppBar className={classes.appBar} data-testid="appBar">
      <Toolbar disableGutters>
        <Box className={classes.navContainer}>{children}</Box>
        <Box className={classes.headerWrapper}>
          <Grid item xs={12} md={3} sm={6} className={classes.title}>
            <Box className={classes.titleWrapper}>
              <Box className={classes.breadcrumbWrapper}>
                <CustomBreadcrumbs
                  product={product}
                  fid={Number(fid)}
                  tid={tid}
                />
              </Box>
              {isHomePage && (
                <Typography
                  className={classes.headerTitle}
                  variant="h4"
                  data-testid="headerTitle"
                >
                  {constants.overviewTitle}
                </Typography>
              )}
            </Box>
            {isHomePage && (
              <div className={classes.inputWrapper}>
                <Button
                  data-testid={"Filters"}
                  variant="outlined"
                  className={classes.filterBtn}
                  onClick={() =>
                    handleFilterChange({
                      target: {
                        dataset: { value: constants.advanceFilters },
                      },
                    })
                  }
                  endIcon={<FilterAltIcon className={classes.filterIcon} />}
                >
                  {constants.filters}
                </Button>
                <Dropdown
                  dataTestid="sortBy"
                  Id="select-outlined"
                  value={sortOption}
                  lists={sortBy}
                  setDropdownValue={(event) => setSortOption(event)}
                  label={constants.sortBy}
                  isNavbarOpen={isNavbarOpen}
                />
                <Search
                  showLabel={true}
                  dataTestid="search"
                  label={constants.searchProductLabel}
                  handleSearchQueryChange={handleSearchQueryChange}
                  searchQuery={searchQuery}
                  isNavbarOpen={isNavbarOpen}
                  height={"2.4rem"}
                />
              </div>
            )}
          </Grid>
        </Box>
      </Toolbar>
      {isDrilldown && (
        <CustomTabs isNavbarOpen={isNavbarOpen} product={product} />
      )}
    </AppBar>
  );
};

Header.propTypes = {
  isNavbarOpen: PropTypes.bool,
  handleSearchQueryChange: PropTypes.func,
  searchQuery: PropTypes.string,
  children: PropTypes.node,
};

export default Header;
